import { useState, useEffect } from "react";
import { RefreshCw, X, Folder, FolderOpen, ChevronRight, ChevronDown, Home } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export const MoveModal = ({ item, onClose, onMove, currentFolderId }) => {
    const [loading, setLoading] = useState(false);
    const [loadingFolders, setLoadingFolders] = useState(true);
    const [folders, setFolders] = useState([]);
    const [selectedFolderId, setSelectedFolderId] = useState(null); // null = root
    const [expandedFolders, setExpandedFolders] = useState(new Set());
    const { FILE_APP_URL } = useAuth();

    const isFolder = item.isFolder || (!item.file_size && item.created_by);

    // Fetch all folders recursively
    useEffect(() => {
        const fetchAllFolders = async () => {
            try {
                setLoadingFolders(true);
                const response = await fetch(`${FILE_APP_URL}/folders?all=true`, {
                    credentials: 'include',
                });

                if (response.ok) {
                    const data = await response.json();
                    // Filter out the item being moved (if it's a folder) and its children
                    const filteredFolders = isFolder
                        ? filterOutFolderAndChildren(data.folders || [], item.id)
                        : (data.folders || []);
                    setFolders(filteredFolders);
                }
            } catch (err) {
                console.error('Failed to fetch folders:', err);
            } finally {
                setLoadingFolders(false);
            }
        };

        fetchAllFolders();
    }, [FILE_APP_URL, item.id, isFolder]);

    // Filter out a folder and all its descendants
    const filterOutFolderAndChildren = (folderList, folderId) => {
        const childIds = new Set();

        const collectChildren = (parentId) => {
            childIds.add(parentId);
            folderList.forEach(f => {
                if (f.parent_folder_id === parentId) {
                    collectChildren(f.id);
                }
            });
        };

        collectChildren(folderId);
        return folderList.filter(f => !childIds.has(f.id));
    };

    // Build folder tree structure
    const buildFolderTree = (folders, parentId = null) => {
        return folders
            .filter(f => f.parent_folder_id === parentId)
            .map(folder => ({
                ...folder,
                children: buildFolderTree(folders, folder.id)
            }));
    };

    const folderTree = buildFolderTree(folders);

    const toggleExpand = (folderId) => {
        setExpandedFolders(prev => {
            const next = new Set(prev);
            if (next.has(folderId)) {
                next.delete(folderId);
            } else {
                next.add(folderId);
            }
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Can't move to same location
        const currentParent = isFolder ? item.parent_folder_id : item.folder_id;
        if (selectedFolderId === currentParent) {
            return;
        }

        setLoading(true);
        try {
            await onMove(selectedFolderId);
        } finally {
            setLoading(false);
        }
    };

    const renderFolderItem = (folder, depth = 0) => {
        const hasChildren = folder.children && folder.children.length > 0;
        const isExpanded = expandedFolders.has(folder.id);
        const isSelected = selectedFolderId === folder.id;
        const isCurrentLocation = isFolder
            ? item.parent_folder_id === folder.id
            : item.folder_id === folder.id;

        return (
            <div key={folder.id}>
                <div
                    className={`flex items-center py-2 px-3 rounded-lg cursor-pointer transition-colors ${
                        isSelected
                            ? 'bg-blue-100 text-blue-700'
                            : isCurrentLocation
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'hover:bg-gray-100'
                    }`}
                    style={{ paddingLeft: `${12 + depth * 20}px` }}
                    onClick={() => !isCurrentLocation && setSelectedFolderId(folder.id)}
                >
                    {hasChildren ? (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(folder.id);
                            }}
                            className="p-1 hover:bg-gray-200 rounded mr-1"
                        >
                            {isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                            ) : (
                                <ChevronRight className="h-4 w-4" />
                            )}
                        </button>
                    ) : (
                        <div className="w-6 mr-1" />
                    )}
                    {isExpanded ? (
                        <FolderOpen className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
                    ) : (
                        <Folder className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
                    )}
                    <span className="truncate flex-1">{folder.name}</span>
                    {isCurrentLocation && (
                        <span className="text-xs text-gray-400 ml-2">(current)</span>
                    )}
                </div>
                {hasChildren && isExpanded && (
                    <div>
                        {folder.children.map(child => renderFolderItem(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    const currentParent = isFolder ? item.parent_folder_id : item.folder_id;
    const isRootCurrentLocation = currentParent === null || currentParent === undefined;
    const canMove = selectedFolderId !== currentParent &&
                    !(selectedFolderId === null && isRootCurrentLocation);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[80vh] flex flex-col">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Move {isFolder ? 'Folder' : 'File'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-gray-100 rounded-md"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                    Select a destination for "<span className="font-medium">{item.name}</span>"
                </p>

                <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                    <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg mb-4">
                        {loadingFolders ? (
                            <div className="flex items-center justify-center py-8">
                                <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                            </div>
                        ) : (
                            <div className="p-2">
                                {/* Root option */}
                                <div
                                    className={`flex items-center py-2 px-3 rounded-lg cursor-pointer transition-colors ${
                                        selectedFolderId === null && !isRootCurrentLocation
                                            ? 'bg-blue-100 text-blue-700'
                                            : isRootCurrentLocation
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'hover:bg-gray-100'
                                    }`}
                                    onClick={() => !isRootCurrentLocation && setSelectedFolderId(null)}
                                >
                                    <Home className="h-5 w-5 text-gray-500 mr-2" />
                                    <span className="font-medium">My Files (Root)</span>
                                    {isRootCurrentLocation && (
                                        <span className="text-xs text-gray-400 ml-2">(current)</span>
                                    )}
                                </div>

                                {/* Folder tree */}
                                {folderTree.map(folder => renderFolderItem(folder))}

                                {folders.length === 0 && (
                                    <p className="text-sm text-gray-500 text-center py-4">
                                        No folders available
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                            disabled={loading || loadingFolders || !canMove}
                        >
                            {loading ? (
                                <div className="flex items-center">
                                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                                    Moving...
                                </div>
                            ) : (
                                'Move Here'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
